import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import "../styles/global.css";
import "../styles/resources.css";
import { Helmet } from "react-helmet";
const Resources = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = "Resources";

    // Initialize AdSense
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }
  }, []);

  const categories = [
    { id: "all", name: "All Resources" },
    { id: "communication", name: "Communication" },
    { id: "conflict", name: "Conflict Resolution" },
    { id: "growth", name: "Personal Growth" },
    { id: "transitions", name: "Relationship Transitions" },
  ];

  const resources = [
    {
      id: "communication-techniques",
      title: "Effective Communication Techniques",
      description:
        "Learn research-backed methods to improve how you express needs and listen to your partner.",
      category: "communication",
      type: "guide",
      image: "assets/images/resource-communication.jpeg",
      featured: true,
    },
    {
      id: "nonviolent-communication",
      title: "Nonviolent Communication in Relationships",
      description:
        "A practical approach to speaking and listening that leads to giving and receiving compassionately.",
      category: "communication",
      type: "article",
      image: "assets/images/resource-nonviolent.jpeg",
      featured: false,
    },
    {
      id: "conflict-resolution",
      title: "Healthy Conflict Resolution",
      description:
        "Convert disagreements into opportunities for growth and deeper understanding.",
      category: "conflict",
      type: "guide",
      image: "assets/images/resource-conflict.jpeg",
      featured: true,
    },
    {
      id: "fair-fighting",
      title: "Fair Fighting Rules",
      description:
        "Guidelines for handling disagreements respectfully and productively.",
      category: "conflict",
      type: "checklist",
      image: "assets/images/resource-fair-fighting.jpeg",
      featured: false,
    },
    {
      id: "self-worth",
      title: "Building Self-Worth",
      description:
        "Develop a strong sense of self-value independent of relationship status.",
      category: "growth",
      type: "workbook",
      image: "assets/images/resource-self-worth.jpeg",
      featured: true,
    },
    {
      id: "attachment-styles",
      title: "Understanding Attachment Styles",
      description:
        "How childhood experiences shape adult relationship patterns and what you can do about it.",
      category: "growth",
      type: "article",
      image: "assets/images/resource-attachment.jpeg",
      featured: true,
    },
    {
      id: "breakup-recovery",
      title: "Healing After a Breakup",
      description:
        "A step-by-step guide to processing grief and rebuilding after a relationship ends.",
      category: "transitions",
      type: "guide",
      image: "assets/images/resource-breakup.jpeg",
      featured: true,
    },
    {
      id: "setting-boundaries",
      title: "Setting Healthy Boundaries",
      description:
        "Learn to establish and maintain boundaries that honor your needs while respecting others.",
      category: "growth",
      type: "workbook",
      image: "assets/images/boundaries.jpeg",
      featured: false,
    },
    {
      id: "red-flags",
      title: "Recognizing Relationship Red Flags",
      description:
        "Learn to identify warning signs of unhealthy or potentially abusive relationships early.",
      category: "transitions",
      type: "checklist",
      image: "assets/images/resource-red-flags.jpeg",
      featured: false,
    },
    {
      id: "active-listening",
      title: "The Art of Active Listening",
      description:
        "Transform your conversations by truly hearing what your partner is communicating.",
      category: "communication",
      type: "guide",
      image: "assets/images/resource-listening.jpeg",
      featured: false,
    },
    {
      id: "love-languages",
      title: "The Five Love Languages",
      description:
        "Discover how you and your partner prefer to give and receive love for deeper connection.",
      category: "communication",
      type: "article",
      image: "assets/images/resource-love-languages.jpeg",
      featured: false,
    },
    {
      id: "moving-forward",
      title: "Moving Forward After Infidelity",
      description:
        "Guidance for both partners on healing and rebuilding trust when possible.",
      category: "transitions",
      type: "workbook",
      image: "assets/images/resource-infidelity.jpeg",
      featured: false,
    },
  ];

  const typeIcons = {
    guide: "fa-book",
    article: "fa-file-alt",
    workbook: "fa-tasks",
    checklist: "fa-check-square",
  };

  const filteredResources = resources.filter((resource) => {
    const matchesCategory =
      activeCategory === "all" || resource.category === activeCategory;
    const matchesSearch =
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  const featuredResources = resources
    .filter((resource) => resource.featured)
    .slice(0, 3);

  return (
    <div className="resources-container">
      <Header />
      <Helmet>
        <title>Resources</title>
        <meta
          name="description"
          content="Various Resources about relationship articles, guides etc to help you."
        />
      </Helmet>
      <section className="resources-hero">
        <div className="resources-hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            Relationship Resources
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lead-text">
            Evidence-based tools and guides to help you navigate relationship
            challenges and foster growth.
          </motion.p>

          <motion.div
            className="search-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}>
            <input
              aria-label="Search resources"
              type="text"
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button className="search-button">
              <i className="fas fa-search"></i>
              search
            </button>
          </motion.div>
        </div>
      </section>

      <section className="featured-resources">
        <div className="container">
          <h2 className="section-title">Featured Resources</h2>

          <div className="featured-grid">
            {featuredResources.map((resource, index) => (
              <motion.div
                key={resource.id}
                className="featured-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}>
                <div className="featured-image">
                  <img src={resource.image} alt={resource.title} />
                  <div className="type-badge" style={{ color: "#000" }}>
                    <i className={`fas ${typeIcons[resource.type]}`}></i>
                    <span>{resource.type}</span>
                  </div>
                </div>
                <div className="featured-content">
                  <h3>{resource.title}</h3>
                  <p>{resource.description}</p>
                  <Link
                    to={`/resources/${resource.id}`}
                    className="view-resource-link">
                    <span>View Resource</span>
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Ad Placement */}
      <div className="container">
        <div
          className="ad-container resources-top-ad"
          style={{ width: "100%", minWidth: "300px" }}>
          {/* <div className="ad-label">Advertisement</div> */}
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-2067522743161930"
            data-ad-slot="1234567896"
            data-ad-format="auto"
            data-full-width-responsive="true"></ins>
        </div>
      </div>

      <section className="all-resources">
        <div className="container">
          <div className="resources-header">
            <h2 className="section-title">Browse All Resources</h2>

            <div className="category-filters">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-filter ${
                    activeCategory === category.id ? "active" : ""
                  }`}
                  onClick={() => setActiveCategory(category.id)}>
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {filteredResources.length === 0 ? (
            <div className="no-results">
              <i className="fas fa-search"></i>
              <h3>No resources found</h3>
              <p>Try adjusting your search or filter criteria</p>
              <button
                className="reset-button"
                onClick={() => {
                  setSearchTerm("");
                  setActiveCategory("all");
                }}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="resources-grid">
              {filteredResources.map((resource, index) => (
                <motion.div
                  key={resource.id}
                  className="resource-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.05 * index }}>
                  <div className="resource-image">
                    <img src={resource.image} alt={resource.title} />
                    <div className="type-tag">
                      <i className={`fas ${typeIcons[resource.type]}`}></i>
                      {resource.type}
                    </div>
                  </div>
                  <div className="resource-content">
                    <div className="category-tag">
                      {categories.find((c) => c.id === resource.category).name}
                    </div>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    <Link
                      to={`/resources/${resource.id}`}
                      className="resource-link">
                      Read More
                      <i className="fas fa-arrow-right"></i>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Bottom Ad Placement */}
          <div
            className="ad-container resources-bottom-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567897"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>
        </div>
      </section>

      <section className="resources-cta">
        <div className="container">
          <div className="cta-box">
            <div className="cta-content">
              <h2>Need Personalized Guidance?</h2>
              <p style={{ color: "#FFF" }}>
                Our relationship assessment provides tailored insights based on
                your unique situation.
              </p>
              <Link to="/quiz" className="cta-button">
                <span>Take Free Assessment</span>
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <div className="cta-image">
              <img
                src="assets/images/assessment-preview.jpeg"
                alt="Assessment preview"
              />
            </div>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <h3 style={{ color: "#FFF" }}>Relationship Wellness</h3>
              <p style={{ color: "#FFF" }}>
                Evidence-based insights for healthier relationships.
              </p>
              <div className="social-icons">
                <a href="#" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="#" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="#" aria-label="Twitter">
                  <i className="fab fa-twitter"></i>
                </a>
                <a href="#" aria-label="LinkedIn">
                  <i className="fab fa-linkedin-in"></i>
                </a>
              </div>
            </div>
            <div className="footer-links">
              <h4 style={{ color: "#FFF" }}>Quick Links</h4>
              <ul>
                <li>
                  <a href="/">Home</a>
                </li>
                <li>
                  <a href="/about">About Us</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <a href="/resources">Resources</a>
                </li>
                <li>
                  <a href="/contact">Contact</a>
                </li>
              </ul>
            </div>
            <div className="footer-contact">
              <h4 style={{ color: "#FFF" }}>Contact Us</h4>
              <p>
                <i className="fas fa-envelope"></i> support@nivexa.net
              </p>
              <p>
                <i className="fas fa-phone"></i> (555) 123-4567
              </p>
              <p>
                <i className="fas fa-map-marker-alt"></i> 123 Wellness Ave,
                Suite 200
                <br />
                San Francisco, CA 94110
              </p>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="copyright">
              © {new Date().getFullYear()} Relationship Wellness. All rights
              reserved.
            </p>
            <div className="legal-links">
              <a href="/privacy">Privacy Policy</a>
              <a href="/terms">Terms of Use</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
